class SearchDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('[data-search-drawer-overlay]');
    this.panel = this.querySelector('[data-search-drawer-panel]');
    this.closeButtons = this.querySelectorAll('[data-search-drawer-close]');
    this.clearButton = this.querySelector('[data-search-drawer-clear]');
    this.emptyState = this.querySelector('[data-search-drawer-empty]');
    this.footer = this.querySelector('[data-search-drawer-footer]');
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearch = this.querySelector('predictive-search');
    this.activeElement = null;
    this._shieldTimer = null;
    this._openingGestureTimer = null;
    this._openingPointerId = null;
    this._scrollLocked = false;
    this._lockScrollY = 0;

    this.onKeyUp = this.onKeyUp.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.onPopularClick = this.onPopularClick.bind(this);
    this.onResultsChange = this.onResultsChange.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentIconPointerDown = this.onDocumentIconPointerDown.bind(this);
    this.onDocumentProxyActivate = this.onDocumentProxyActivate.bind(this);
    this.onOpeningGestureEnd = this.onOpeningGestureEnd.bind(this);
    this.onOpeningGestureClick = this.onOpeningGestureClick.bind(this);
    this.onClearClick = this.onClearClick.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
  }

  connectedCallback() {
    // Header section uses transform (sticky tuck), which creates a containing
    // block for position:fixed — move drawer to body like cart-modal.
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
      return; // reconnecting will run connectedCallback again
    }

    if (this.dataset.searchDrawerReady === 'true') return;
    this.dataset.searchDrawerReady = 'true';

    if (this.overlay) {
      this.overlay.addEventListener('click', this.onOverlayClick);
    }
    this.closeButtons.forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
      });
    });
    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.onClearClick);
    }
    if (this.input) {
      this.input.addEventListener('input', this.onInputChange);
    }
    this.addEventListener('keyup', this.onKeyUp);
    this.querySelectorAll('[data-search-popular]').forEach((btn) => {
      btn.addEventListener('click', this.onPopularClick);
    });
    document.addEventListener('search-drawer:results', this.onResultsChange);
    document.addEventListener('click', this.onDocumentClick);
    // Keep the desktop search icon from taking focus on pointerdown
    document.addEventListener('pointerdown', this.onDocumentIconPointerDown, true);
    // Capture so we open + focus in the same user gesture (iOS keyboard)
    document.addEventListener('pointerdown', this.onDocumentProxyActivate, true);
    document.addEventListener('focusin', this.onDocumentProxyActivate, true);
    this.syncClearButton();
  }

  disconnectedCallback() {
    document.removeEventListener('search-drawer:results', this.onResultsChange);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('pointerdown', this.onDocumentIconPointerDown, true);
    document.removeEventListener('pointerdown', this.onDocumentProxyActivate, true);
    document.removeEventListener('focusin', this.onDocumentProxyActivate, true);
    this.clearOpeningGestureGuard();
    if (this._shieldTimer) window.clearTimeout(this._shieldTimer);
  }

  isMobileViewport() {
    return window.matchMedia('(max-width: 63.99em)').matches;
  }

  onDocumentIconPointerDown(event) {
    const trigger = event.target.closest('[data-search-drawer-open]');
    if (!trigger || this.contains(trigger)) return;
    if (trigger.matches('[data-search-drawer-proxy], input')) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    // Keep the icon from taking focus, and open in this same gesture so the
    // drawer field can receive the caret (click-only was one step too late).
    event.preventDefault();
    this.open({
      instant: this.isMobileViewport(),
      triggeredBy: trigger,
      syncFocus: true,
    });
  }

  onDocumentClick(event) {
    const trigger = event.target.closest('[data-search-drawer-open]');
    if (!trigger) return;
    // Ignore clicks that originated inside an already-open drawer panel
    if (this.contains(trigger)) return;
    // Real inputs use proxy handlers instead
    if (trigger.matches('[data-search-drawer-proxy], input')) return;
    event.preventDefault();
    event.stopPropagation();

    // Mobile: no slide + focus in this gesture. Desktop keeps the panel slide.
    this.open({
      instant: this.isMobileViewport(),
      triggeredBy: trigger,
      syncFocus: true,
    });
  }

  onDocumentProxyActivate(event) {
    const proxy = event.target.closest('[data-search-drawer-proxy]');
    if (!proxy || this.contains(proxy)) return;
    // Closing returns focus / blurs; ignore proxy reactivation for a beat
    if (this._ignoreProxyUntil && Date.now() < this._ignoreProxyUntil) return;

    // pointerdown opens immediately; focusin covers keyboard tabbing
    if (event.type === 'pointerdown') {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      // Prevent the proxy from taking focus so we can focus the drawer field
      // in this same gesture (required for the iOS keyboard).
      event.preventDefault();
      // Keep the opening finger on the proxy so pointerup / the synthetic click
      // don't land on newly painted drawer content underneath (Popular, etc.).
      this.armOpeningGestureGuard(proxy, event);
    } else if (event.type === 'focusin') {
      // Ignore focusin recovery that lands on the menu chrome proxy when focus
      // came from outside the open nav (nav focus-trap / sticky-header churn).
      const openNav = proxy.closest('.header__nav.active');
      if (openNav) {
        const from = event.relatedTarget;
        if (!(from instanceof Node) || !openNav.contains(from)) return;
      }
    }

    this.open({
      instant: true,
      triggeredBy: proxy,
      syncFocus: true,
    });
  }

  /**
   * After opening from the header/menu proxy on pointerdown, the same finger's
   * pointerup + compatibility click can retarget to drawer UI. Capture the
   * pointer on the proxy and briefly swallow matching end events.
   */
  armOpeningGestureGuard(proxy, event) {
    const pointerId = event.pointerId;
    if (pointerId == null) return;

    this.clearOpeningGestureGuard();
    this._openingPointerId = pointerId;

    if (typeof proxy.setPointerCapture === 'function') {
      try {
        proxy.setPointerCapture(pointerId);
      } catch (error) {
        console.error('SearchDrawer: failed to capture opening pointer', error);
      }
    }

    document.addEventListener('pointerup', this.onOpeningGestureEnd, true);
    document.addEventListener('pointercancel', this.onOpeningGestureEnd, true);
    document.addEventListener('click', this.onOpeningGestureClick, true);

    this._openingGestureTimer = window.setTimeout(() => {
      this.clearOpeningGestureGuard();
    }, 450);
  }

  onOpeningGestureEnd(event) {
    if (this._openingPointerId == null || event.pointerId !== this._openingPointerId) return;
    event.preventDefault();
    event.stopPropagation();
    // Keep the click listener only long enough for the synthetic click after
    // pointerup — a genuine second tap must not be blocked.
    document.removeEventListener('pointerup', this.onOpeningGestureEnd, true);
    document.removeEventListener('pointercancel', this.onOpeningGestureEnd, true);
    if (this._openingGestureTimer) {
      window.clearTimeout(this._openingGestureTimer);
    }
    this._openingGestureTimer = window.setTimeout(() => {
      this.clearOpeningGestureGuard();
    }, 100);
  }

  onOpeningGestureClick(event) {
    if (this._openingPointerId == null) return;
    // Only consume the compatibility click that follows the opening pointer.
    event.preventDefault();
    event.stopPropagation();
    this.clearOpeningGestureGuard();
  }

  clearOpeningGestureGuard() {
    document.removeEventListener('pointerup', this.onOpeningGestureEnd, true);
    document.removeEventListener('pointercancel', this.onOpeningGestureEnd, true);
    document.removeEventListener('click', this.onOpeningGestureClick, true);
    if (this._openingGestureTimer) {
      window.clearTimeout(this._openingGestureTimer);
      this._openingGestureTimer = null;
    }
    this._openingPointerId = null;
  }

  onKeyUp(event) {
    if (event.code === 'Escape') this.close();
  }

  onOverlayClick() {
    this.close();
  }

  onPopularClick(event) {
    const term = event.currentTarget.getAttribute('data-search-popular');
    if (!term || !this.input) return;
    this.input.value = term;
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.input.focus();
    this.syncClearButton();
  }

  onInputChange() {
    this.syncClearButton();
  }

  syncClearButton() {
    if (!this.clearButton) return;
    const hasQuery = Boolean(this.input?.value?.trim());
    this.clearButton.hidden = !hasQuery;
  }

  /**
   * Clear query, predictive results, and proxy fields so the next open
   * always starts at the empty state (Popular / You May Also Like).
   */
  resetSearchState() {
    if (this.input) {
      this.input.value = '';
    }

    if (this.predictiveSearch && typeof this.predictiveSearch.close === 'function') {
      try {
        this.predictiveSearch.close(true);
      } catch (error) {
        console.error('SearchDrawer: failed to reset predictive search', error);
      }
    }

    document.querySelectorAll('[data-search-drawer-proxy]').forEach((proxy) => {
      if (proxy !== this.input) proxy.value = '';
    });

    if (this.input) {
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    this.syncClearButton();
    this.dispatchResultsState();
  }

  onClearClick(event) {
    event.preventDefault();
    this.resetSearchState();
    this.focusInput();
  }

  onResultsChange(event) {
    if (event.detail?.source !== this) return;
    const hasQuery = Boolean(event.detail.hasQuery);

    if (this.emptyState) {
      this.emptyState.hidden = hasQuery;
    }
    if (this.footer) {
      this.footer.hidden = !hasQuery;
    }
  }

  setActiveElement(el) {
    this.activeElement = el;
  }

  /**
   * Closing the mobile nav mid-tap can let the click fall through to the
   * basket icon underneath (same right-side hit target). Briefly disable
   * pointer events on cart + header chrome while search takes over.
   */
  shieldGhostClicks() {
    const cart = document.querySelector('cart-drawer');
    const header = document.getElementById('shopify-section-header');
    const shielded = [cart, header].filter(Boolean);

    if (cart?.classList.contains('active') && typeof cart.close === 'function') {
      try {
        cart.close();
      } catch (error) {
        console.error('SearchDrawer: failed to close cart drawer', error);
      }
    }

    shielded.forEach((el) => {
      el.style.pointerEvents = 'none';
    });

    if (this._shieldTimer) window.clearTimeout(this._shieldTimer);
    this._shieldTimer = window.setTimeout(() => {
      shielded.forEach((el) => {
        el.style.removeProperty('pointer-events');
      });
      this._shieldTimer = null;
    }, 450);
  }

  closeMobileNav() {
    const nav = document.querySelector('#siteHeader .header__nav');
    // Desktop quick nav is this same node and is not a drawer. Only close a real open mobile nav.
    if (!nav || !nav.classList.contains('active')) return;

    const mobileClose = document.querySelector('modal-toggle[target=".header__nav"][action="close"]');
    if (mobileClose && typeof mobileClose.close === 'function') {
      try {
        mobileClose.close();
      } catch (err) {
        console.error('SearchDrawer: failed to close mobile nav', err);
      }
    }
  }

  /**
   * Pin the page while search is open so iOS keyboard resize can’t create
   * horizontal overflow (which Safari “zooms out” to fit, revealing the cart).
   */
  lockPageScroll() {
    if (this._scrollLocked) return;
    this._scrollLocked = true;
    this._lockScrollY = window.scrollY || window.pageYOffset || 0;

    document.documentElement.classList.add('search-drawer-scroll-lock');
    document.body.classList.add('overflow-hidden');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._lockScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  unlockPageScroll() {
    if (!this._scrollLocked) return;
    this._scrollLocked = false;

    document.documentElement.classList.remove('search-drawer-scroll-lock');
    document.body.classList.remove('overflow-hidden');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('left');
    document.body.style.removeProperty('right');
    document.body.style.removeProperty('width');

    const y = this._lockScrollY || 0;
    this._lockScrollY = 0;
    window.scrollTo(0, y);

    // Clear any horizontal drift left by the iOS keyboard / 100vw drawers
    document.documentElement.scrollLeft = 0;
    document.body.scrollLeft = 0;
    if (document.scrollingElement) document.scrollingElement.scrollLeft = 0;
  }

  setFloatingChromeVisible(visible) {
    document.querySelectorAll('#userwayAccessibilityIcon, #gorgias-chat-container').forEach((el) => {
      if (visible) {
        // Match cart-drawer / mobile nav — don't restore while cart is open
        if (document.querySelector('cart-drawer.active')) return;
        el.style.display = '';
        el.style.removeProperty('visibility');
        el.style.removeProperty('pointer-events');
      } else {
        el.style.display = 'none';
        el.style.visibility = 'hidden';
        el.style.pointerEvents = 'none';
      }
    });

    try {
      if (window.GorgiasChat && typeof window.GorgiasChat.hideChat === 'function') {
        const init = window.GorgiasChat.init ? window.GorgiasChat.init() : Promise.resolve();
        Promise.resolve(init)
          .then(() => {
            window.GorgiasChat.hideChat(!visible);
          })
          .catch((error) => {
            console.error('SearchDrawer: GorgiasChat hide/show failed', error);
          });
      }
    } catch (error) {
      console.error('SearchDrawer: GorgiasChat toggle failed', error);
    }
  }

  focusInput() {
    if (!this.classList.contains('is-open')) return;
    // Don't trust the constructor cache — the input lives inside predictive-search
    // and may not have been queryable when the element first upgraded.
    this.input = this.querySelector('input[type="search"]') || this.input;
    const focusTarget = this.input || this.panel;
    if (!focusTarget) return;

    /*
      Focus the input first with a plain .focus() (no preventScroll) so iOS
      treats it as part of the user gesture and opens the keyboard. Then install
      the focus trap around that already-focused field.
    */
    try {
      focusTarget.focus();
    } catch (error) {
      console.error('SearchDrawer: failed to focus search input', error);
    }

    if (typeof trapFocus === 'function' && this.panel) {
      try {
        trapFocus(this.panel, focusTarget);
      } catch (trapError) {
        console.error('SearchDrawer: trapFocus failed', trapError);
      }
    }
  }

  /**
   * Desktop opens with a slide. Immediate focus can fail or get stolen while
   * the panel is still off-canvas / mid-transition, which left shoppers having
   * to click the "Search Weston Table" field before typing. Retry until the
   * input is actually active.
   */
  scheduleDesktopFocus() {
    const focusWhenReady = () => {
      this.focusInput();
      if (this.input && document.activeElement === this.input) return;
      window.setTimeout(() => this.focusInput(), 50);
      window.setTimeout(() => this.focusInput(), 150);
      window.setTimeout(() => this.focusInput(), 400);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(focusWhenReady);
    });

    if (!this.panel) return;

    const onTransitionEnd = (event) => {
      if (event.target !== this.panel || event.propertyName !== 'transform') return;
      this.panel.removeEventListener('transitionend', onTransitionEnd);
      this.focusInput();
    };
    this.panel.addEventListener('transitionend', onTransitionEnd);
  }

  open(options = {}) {
    const triggeredBy = options instanceof Element ? options : options.triggeredBy;
    const instant = Boolean(options && options.instant);

    if (this.classList.contains('is-open')) {
      if (triggeredBy && triggeredBy.matches?.('input[data-search-drawer-proxy]') && this.input) {
        this.input.value = triggeredBy.value || this.input.value;
      }
      this.focusInput();
      return;
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);

    if (typeof window.wtPromoteDeferredMedia === 'function') {
      window.wtPromoteDeferredMedia(this);
    }

    this.shieldGhostClicks();
    this.closeMobileNav();

    if (instant) {
      this.classList.add('is-instant');
    } else {
      this.classList.remove('is-instant');
    }

    // Copy any text already typed in the menu proxy field
    if (triggeredBy && triggeredBy.matches?.('input[data-search-drawer-proxy]') && this.input) {
      this.input.value = triggeredBy.value || '';
    }

    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.documentElement.setAttribute('data-active-modal', 'search');
    this.lockPageScroll();

    this.setFloatingChromeVisible(false);

    /*
      Focus must happen in the same user gesture for iOS to show the keyboard.
      Always sync-focus from click/proxy handlers. On desktop the panel slides,
      so also retry after the transition — otherwise the field looks ready
      ("Search Weston Table") but isn't focused until a second click.
    */
    this.focusInput();
    if (!instant) {
      this.scheduleDesktopFocus();
    }

    // Clear proxy fields so the menu doesn't keep a stale query
    document.querySelectorAll('[data-search-drawer-proxy]').forEach((proxy) => {
      if (proxy !== this.input) proxy.value = '';
    });

    this.syncClearButton();
    this.dispatchResultsState();
  }

  close() {
    if (!this.classList.contains('is-open')) return;

    /*
      Opening from a header/menu proxy stores that input as activeElement.
      removeTrapFocus() would refocus it → focusin → proxy handler → reopen.
      Never return focus to a proxy; blur the drawer field first.
    */
    this._ignoreProxyUntil = Date.now() + 500;
    if (this.input && typeof this.input.blur === 'function') {
      try {
        this.input.blur();
      } catch (error) {
        console.error('SearchDrawer: failed to blur search input', error);
      }
    }

    const returnFocusTo =
      this.activeElement && !this.activeElement.matches?.('[data-search-drawer-proxy]')
        ? this.activeElement
        : null;

    this.resetSearchState();

    this.classList.remove('is-open');
    this.classList.remove('is-instant');
    this.setAttribute('aria-hidden', 'true');
    document.documentElement.removeAttribute('data-active-modal');
    this.unlockPageScroll();

    this.setFloatingChromeVisible(true);

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(returnFocusTo);
    } else if (returnFocusTo && typeof returnFocusTo.focus === 'function') {
      returnFocusTo.focus();
    }
  }

  dispatchResultsState() {
    const hasQuery = Boolean(this.input?.value?.trim());
    const hasResults = this.predictiveSearch?.getAttribute('results') === 'true';
    document.dispatchEvent(
      new CustomEvent('search-drawer:results', {
        detail: { source: this, hasQuery, hasResults },
      })
    );
  }
}

customElements.define('search-drawer', SearchDrawer);
